import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Table, TableBody, TableCell, TableFooter, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { Checkbox } from '../components/ui/checkbox';
import { Scale, RefreshCw, Download } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { companyAPI } from '../services/api';

const todayIso = () => new Date().toISOString().slice(0, 10);
const yearStartIso = () => `${new Date().getFullYear()}-01-01`;

const ACCOUNT_TYPE_LABEL = {
  A: 'Asset', E: 'Expense', L: 'Liability', I: 'Income', S: 'Equity',
};

const TrialBalance = () => {
  const navigate = useNavigate();
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const [companyCodes, setCompanyCodes] = useState([]);
  const [compareMode, setCompareMode] = useState(false);
  const [filters, setFilters] = useState({
    company_code_id: '',
    fiscal_year: String(new Date().getFullYear()),
    posting_date_from: yearStartIso(),
    posting_date_to: todayIso(),
    include_reversed: false,
    // period comparison
    prior_from: `${new Date().getFullYear() - 1}-01-01`,
    prior_to: `${new Date().getFullYear() - 1}-12-31`,
  });
  const [rows, setRows] = useState([]);
  const [compareRows, setCompareRows] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const cc = await companyAPI.getCompanyCodes();
        setCompanyCodes(cc.data || []);
        if (cc.data?.length) setFilters((f) => ({ ...f, company_code_id: cc.data[0].company_code_id }));
      } catch (e) { toast.error('Failed to load company codes'); }
    })();
  }, []);

  const runReport = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => {
        if (v === '' || v === false || v === null) return;
        if (compareMode) {
          if (['prior_from', 'prior_to'].includes(k)) params[k] = v;
          if (k === 'posting_date_from') params.current_from = v;
          if (k === 'posting_date_to') params.current_to = v;
          if (['company_code_id', 'fiscal_year', 'include_reversed'].includes(k)) params[k] = v;
        } else {
          if (['prior_from', 'prior_to'].includes(k)) return;
          params[k] = v;
        }
      });
      if (compareMode) {
        const r = await axios.get(`${backendUrl}/api/fi/reports/trial-balance-compared`, { params });
        setCompareRows(r.data || []);
        setRows([]);
      } else {
        const r = await axios.get(`${backendUrl}/api/fi/reports/trial-balance`, { params });
        setRows(r.data || []);
        setCompareRows([]);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load trial balance');
    } finally { setLoading(false); }
  }, [backendUrl, filters, compareMode]);

  useEffect(() => { if (filters.company_code_id) runReport(); }, [filters.company_code_id, compareMode, runReport]);

  const totals = rows.reduce(
    (a, r) => {
      a.debit += r.total_debit; a.credit += r.total_credit; a.balance += r.balance;
      return a;
    },
    { debit: 0, credit: 0, balance: 0 }
  );
  const balanced = Math.abs(totals.balance) < 0.01;

  const downloadCsv = () => {
    if (!rows.length) return;
    const headers = ['Company', 'GL Account', 'Account Name', 'Type', 'Total Debit', 'Total Credit', 'Net Balance', 'Lines'];
    const lines = rows.map((r) => [
      r.company_code_id, r.gl_account, r.account_name || '', r.account_type || '',
      r.total_debit, r.total_credit, r.balance, r.line_count,
    ].map((v) => `"${(v ?? '').toString().replace(/"/g, '""')}"`).join(','));
    const csv = [headers.join(','), ...lines].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `trial-balance-${filters.company_code_id}-${filters.fiscal_year}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Layout>
      <div className="p-6 max-w-7xl mx-auto" data-testid="trial-balance-page">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-2">
              <Scale className="w-7 h-7 text-orange-600" />
              Trial Balance
            </h1>
            <p className="text-gray-600 mt-1">
              Per-account aggregated debits, credits, and net balance.
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={downloadCsv} disabled={!rows.length} data-testid="tb-export-csv-btn">
              <Download className="w-4 h-4 mr-1" />Export CSV
            </Button>
            <Button onClick={runReport} disabled={loading} data-testid="tb-run-btn">
              <RefreshCw className="w-4 h-4 mr-1" />{loading ? 'Loading…' : 'Run'}
            </Button>
          </div>
        </div>

        <Card className="mb-4">
          <CardHeader><CardTitle>Filters</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-4 gap-3">
            <div>
              <Label>Company Code</Label>
              <Select
                value={filters.company_code_id}
                onValueChange={(v) => setFilters({ ...filters, company_code_id: v })}
              >
                <SelectTrigger data-testid="tb-cc-select"><SelectValue placeholder="Select…" /></SelectTrigger>
                <SelectContent>
                  {companyCodes.map((c) => (
                    <SelectItem key={c.company_code_id} value={c.company_code_id}>
                      {c.company_code_id} - {c.company_code_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Fiscal Year</Label>
              <Input
                value={filters.fiscal_year}
                onChange={(e) => setFilters({ ...filters, fiscal_year: e.target.value })}
                data-testid="tb-fy-input"
              />
            </div>
            <div>
              <Label>Posting Date From</Label>
              <Input
                type="date"
                value={filters.posting_date_from}
                onChange={(e) => setFilters({ ...filters, posting_date_from: e.target.value })}
              />
            </div>
            <div>
              <Label>Posting Date To</Label>
              <Input
                type="date"
                value={filters.posting_date_to}
                onChange={(e) => setFilters({ ...filters, posting_date_to: e.target.value })}
              />
            </div>
            <div className="col-span-4 flex items-center gap-6">
              <div className="flex items-center gap-2">
                <Checkbox
                  checked={filters.include_reversed}
                  onCheckedChange={(v) => setFilters({ ...filters, include_reversed: !!v })}
                />
                <Label>Include reversed documents</Label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  checked={compareMode}
                  onCheckedChange={(v) => setCompareMode(!!v)}
                  data-testid="tb-compare-toggle"
                />
                <Label>Compare with prior period</Label>
              </div>
            </div>
            {compareMode && (
              <>
                <div>
                  <Label>Prior Period From</Label>
                  <Input
                    type="date"
                    value={filters.prior_from}
                    onChange={(e) => setFilters({ ...filters, prior_from: e.target.value })}
                    data-testid="tb-prior-from"
                  />
                </div>
                <div>
                  <Label>Prior Period To</Label>
                  <Input
                    type="date"
                    value={filters.prior_to}
                    onChange={(e) => setFilters({ ...filters, prior_to: e.target.value })}
                    data-testid="tb-prior-to"
                  />
                </div>
                <div className="col-span-2 text-xs text-gray-600 self-end">
                  Comparing <strong>{filters.posting_date_from} → {filters.posting_date_to}</strong> vs{' '}
                  <strong>{filters.prior_from} → {filters.prior_to}</strong>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {compareMode ? (
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Period-over-Period — {compareRows.length} GL Account(s)</CardTitle>
              <div className="text-xs text-gray-500">
                Variance % computed as (current - prior) / |prior| × 100
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-10 text-gray-500">Loading…</div>
              ) : compareRows.length === 0 ? (
                <div className="text-center py-10 text-gray-500">No postings in either period.</div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead rowSpan={2}>GL</TableHead>
                        <TableHead rowSpan={2}>Name</TableHead>
                        <TableHead rowSpan={2}>Type</TableHead>
                        <TableHead colSpan={3} className="text-center border-x bg-orange-50">Current Period</TableHead>
                        <TableHead colSpan={3} className="text-center border-x bg-gray-50">Prior Period</TableHead>
                        <TableHead colSpan={2} className="text-center bg-orange-50">Variance</TableHead>
                      </TableRow>
                      <TableRow>
                        <TableHead className="text-right bg-orange-50">Dr</TableHead>
                        <TableHead className="text-right bg-orange-50">Cr</TableHead>
                        <TableHead className="text-right bg-orange-50">Balance</TableHead>
                        <TableHead className="text-right bg-gray-50">Dr</TableHead>
                        <TableHead className="text-right bg-gray-50">Cr</TableHead>
                        <TableHead className="text-right bg-gray-50">Balance</TableHead>
                        <TableHead className="text-right bg-orange-50">Δ Amount</TableHead>
                        <TableHead className="text-right bg-orange-50">Δ %</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {compareRows.map((r) => {
                        const pos = r.balance_change > 0;
                        const neg = r.balance_change < 0;
                        const colorAmt = pos ? 'text-emerald-700' : neg ? 'text-red-700' : 'text-gray-500';
                        return (
                          <TableRow key={`${r.company_code_id}-${r.gl_account}`} data-testid={`tb-cmp-row-${r.gl_account}`}>
                            <TableCell className="font-mono font-medium">{r.gl_account}</TableCell>
                            <TableCell>{r.account_name || '-'}</TableCell>
                            <TableCell><Badge variant="outline">{ACCOUNT_TYPE_LABEL[r.account_type] || r.account_type || '-'}</Badge></TableCell>
                            <TableCell className="text-right font-mono">{r.current_debit.toLocaleString(undefined, { minimumFractionDigits: 2 })}</TableCell>
                            <TableCell className="text-right font-mono">{r.current_credit.toLocaleString(undefined, { minimumFractionDigits: 2 })}</TableCell>
                            <TableCell className="text-right font-mono">{r.current_balance.toLocaleString(undefined, { minimumFractionDigits: 2 })}</TableCell>
                            <TableCell className="text-right font-mono text-gray-500">{r.prior_debit.toLocaleString(undefined, { minimumFractionDigits: 2 })}</TableCell>
                            <TableCell className="text-right font-mono text-gray-500">{r.prior_credit.toLocaleString(undefined, { minimumFractionDigits: 2 })}</TableCell>
                            <TableCell className="text-right font-mono text-gray-500">{r.prior_balance.toLocaleString(undefined, { minimumFractionDigits: 2 })}</TableCell>
                            <TableCell className={`text-right font-mono font-semibold ${colorAmt}`}>{r.balance_change.toLocaleString(undefined, { minimumFractionDigits: 2 })}</TableCell>
                            <TableCell className={`text-right font-mono ${colorAmt}`}>
                              {r.balance_change_pct === null ? '—' : `${r.balance_change_pct >= 0 ? '+' : ''}${r.balance_change_pct.toFixed(2)}%`}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        ) : (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>{rows.length} GL Account(s)</CardTitle>
            <div className={`text-sm font-medium ${balanced ? 'text-emerald-600' : 'text-red-600'}`}>
              {balanced ? '✓ Books are balanced' : `Δ ${totals.balance.toFixed(2)} — books are NOT balanced`}
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-10 text-gray-500">Loading…</div>
            ) : rows.length === 0 ? (
              <div className="text-center py-10 text-gray-500">No postings match the filters.</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Company</TableHead>
                    <TableHead>GL Account</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead className="text-right">Total Debit</TableHead>
                    <TableHead className="text-right">Total Credit</TableHead>
                    <TableHead className="text-right">Net Balance</TableHead>
                    <TableHead className="text-right">Lines</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((r) => (
                    <TableRow
                      key={`${r.company_code_id}-${r.gl_account}`}
                      data-testid={`tb-row-${r.gl_account}`}
                      className="cursor-pointer hover:bg-orange-50"
                      onClick={() => {
                        const params = new URLSearchParams({
                          company_code_id: r.company_code_id,
                          fiscal_year: filters.fiscal_year,
                          gl_account: r.gl_account,
                          posting_date_from: filters.posting_date_from,
                          posting_date_to: filters.posting_date_to,
                          include_reversed: filters.include_reversed ? '1' : '0',
                        });
                        navigate(`/finance/gl-line-items?${params.toString()}`);
                      }}
                    >
                      <TableCell>{r.company_code_id}</TableCell>
                      <TableCell className="font-mono font-medium">{r.gl_account}</TableCell>
                      <TableCell>{r.account_name || '-'}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{ACCOUNT_TYPE_LABEL[r.account_type] || r.account_type || '-'}</Badge>
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        {r.total_debit.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        {r.total_credit.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                      </TableCell>
                      <TableCell className={`text-right font-mono ${r.balance < 0 ? 'text-red-600' : ''}`}>
                        {r.balance.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                      </TableCell>
                      <TableCell className="text-right font-mono text-xs">{r.line_count}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
                <TableFooter>
                  <TableRow>
                    <TableCell colSpan={4} className="font-bold">TOTALS</TableCell>
                    <TableCell className="text-right font-mono font-bold" data-testid="tb-total-debit">
                      {totals.debit.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </TableCell>
                    <TableCell className="text-right font-mono font-bold" data-testid="tb-total-credit">
                      {totals.credit.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </TableCell>
                    <TableCell className={`text-right font-mono font-bold ${balanced ? 'text-emerald-600' : 'text-red-600'}`} data-testid="tb-net-balance">
                      {totals.balance.toFixed(2)}
                    </TableCell>
                    <TableCell />
                  </TableRow>
                </TableFooter>
              </Table>
            )}
          </CardContent>
        </Card>
        )}
      </div>
    </Layout>
  );
};

export default TrialBalance;
